import React, { useEffect, useRef, useState } from 'react';
import {
  deductCoinsForOrder,
  deductCoinsForPhone,
  lookupCoins,
} from '../api/skipzyAdmin';

const DEFAULT_REASON = 'Customer could not use coins';

// A deduct takes money out of a customer's wallet, so its button is the one
// place the screen gets loud. #B93573 is zoddle-pink deepened until white text
// on it passes 4.5:1 (the brand pink itself is ~3.4:1).
const BTN =
  'inline-flex items-center justify-center gap-2 rounded-md px-4 py-2 text-sm font-semibold ' +
  'transition-[transform,background-color,opacity,filter] duration-150 ease-[cubic-bezier(0.23,1,0.32,1)] ' +
  'active:scale-[0.97] disabled:opacity-50 disabled:active:scale-100 disabled:cursor-not-allowed ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 whitespace-nowrap';
const BTN_DARK = `${BTN} bg-gray-900 text-white hover:bg-gray-700 focus-visible:ring-gray-900/60`;
const BTN_DANGER = `${BTN} bg-[#B93573] text-white hover:brightness-110 focus-visible:ring-[#B93573]/60`;
const BTN_GHOST = `${BTN} border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 focus-visible:ring-gray-400/60`;
const FIELD =
  'w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 ' +
  'placeholder:text-gray-400 transition-colors duration-150 ' +
  'focus:border-gray-400 focus:outline-none focus:ring-2 focus:ring-zoddle-green/40';

// One entrance, used only for results appearing after a search (a response to
// the user's action). Exponential ease-out, no scale-from-zero, and nothing
// moves for people who ask for reduced motion.
const CSS = `
@keyframes cd-enter { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }
.cd-enter { animation: cd-enter 200ms cubic-bezier(0.23, 1, 0.32, 1) both; }
@keyframes cd-pulse { 50% { opacity: .45; } }
.cd-skeleton { animation: cd-pulse 1.4s ease-in-out infinite; }
@media (prefers-reduced-motion: reduce) {
  .cd-enter { animation: none; }
  .cd-skeleton { animation: none; }
}`;

function errorMessage(err, fallback) {
  const detail = err.response?.data?.detail;
  if (!detail) return err.message || fallback;
  if (typeof detail === 'string') return detail;
  if (detail.message) return typeof detail.message === 'string' ? detail.message : JSON.stringify(detail.message);
  return JSON.stringify(detail);
}

const rupees = paise => `₹${(paise / 100).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const when = iso =>
  new Date(iso).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' });
const coins = n => `${n.toLocaleString('en-IN')} ${n === 1 ? 'coin' : 'coins'}`;

// What the box will do with what is typed, shown as the user types.
function describeQuery(q) {
  const t = q.trim();
  if (!t) return null;
  const digits = t.replace(/\D/g, '');
  // Only digits and phone punctuation, and at least ten digits: "+91 98765-43210".
  if (/^[\d\s+()-]+$/.test(t) && digits.length >= 10) {
    return 'Phone number: shows every order this customer has, with the coins each earned.';
  }
  return 'Order or appointment id: deducts exactly the coins that order earned.';
}

function Icon({ name }) {
  const common = { width: 16, height: 16, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round', 'aria-hidden': true };
  if (name === 'search') return <svg {...common}><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>;
  if (name === 'check') return <svg {...common}><path d="m5 12 5 5L20 7" /></svg>;
  return <svg {...common}><path d="M12 8v5M12 17h.01" /><circle cx="12" cy="12" r="9" /></svg>;
}

const PILL_TONES = {
  good: 'bg-green-50 text-green-800',
  bad: 'bg-red-50 text-red-800',
  neutral: 'bg-gray-100 text-gray-700',
};

function pillTone(status) {
  if (['paid', 'completed', 'delivered'].includes(status)) return PILL_TONES.good;
  if (/cancel|fail|refund/.test(status)) return PILL_TONES.bad;
  return PILL_TONES.neutral;
}

function StatusPill({ status }) {
  return (
    <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${pillTone(status)}`}>
      {status.replace(/_/g, ' ')}
    </span>
  );
}

function Customer({ customer }) {
  return (
    <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1">
      <div>
        <span className="font-semibold text-gray-900">{customer.name || 'Unnamed customer'}</span>
        <span className="ml-2 tabular-nums text-sm text-gray-500">{customer.phone}</span>
      </div>
      <div className="text-sm text-gray-500">
        Wallet <b className="tabular-nums text-gray-800">{customer.balance_coins}</b>
        {customer.available_coins !== customer.balance_coins && (
          <> · <b className="tabular-nums text-gray-800">{customer.available_coins}</b> free to deduct</>
        )}
      </div>
    </div>
  );
}

// A two-step control: first click arms it, second click commits. Inline, so no
// browser popup and no modal, and Escape or Cancel backs out. A deduct is not
// idempotent, so the second step is the safety.
function ArmedButton({ label, confirmLabel, armed, onArm, onCancel, onConfirm, busy, disabled, small }) {
  const size = small ? '!px-3 !py-1.5' : '';
  if (!armed) {
    return <button type="button" onClick={onArm} disabled={disabled} className={`${BTN_DANGER} ${size}`}>{label}</button>;
  }
  return (
    <span className="inline-flex items-center gap-2">
      <button type="button" onClick={onConfirm} disabled={busy} className={`${BTN_DANGER} ${size}`}>
        {busy ? 'Deducting…' : confirmLabel}
      </button>
      <button type="button" onClick={onCancel} disabled={busy} className={`${BTN_GHOST} ${size}`}>Cancel</button>
    </span>
  );
}

function OrderPanel({ order, armedId, setArmedId, busyId, onDeduct, reason, setReason }) {
  const { customer } = order;
  const short = order.coins_remaining > customer.available_coins;
  const done = order.coins_remaining === 0;
  const reasonOk = reason.trim().length >= 3;
  return (
    <section className="cd-enter overflow-hidden rounded-xl border border-gray-200 bg-white">
      <div className="flex flex-wrap items-start justify-between gap-4 p-5">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h4 className="text-lg font-semibold tracking-tight text-gray-900">{order.order_number}</h4>
            <StatusPill status={order.status} />
          </div>
          <p className="mt-1 text-sm text-gray-500">
            {order.zocs_job_id ? <>Appointment <b className="text-gray-700">{order.zocs_job_id}</b> · </> : null}
            {when(order.created_at)} · {rupees(order.total_paise)} paid
          </p>
        </div>
        <div className="text-right">
          <div className="text-3xl font-semibold tabular-nums tracking-tight text-gray-900">
            {order.coins_remaining}
            <span className="ml-1.5 text-base font-medium text-gray-500">{order.coins_remaining === 1 ? 'coin' : 'coins'}</span>
          </div>
          <p className="text-sm text-gray-500 tabular-nums">
            {done ? 'nothing left to deduct' : 'will be deducted'} · earned {order.coins_earned}
            {order.coins_deducted > 0 && <>, already taken back {order.coins_deducted}</>}
          </p>
        </div>
      </div>

      <div className="border-t border-gray-100 bg-gray-50/70 px-5 py-4">
        <Customer customer={customer} />
      </div>

      <div className="border-t border-gray-100 px-5 py-4">
        {done ? (
          <p className="flex items-center gap-2 text-sm text-gray-600">
            <span className="text-green-600"><Icon name="check" /></span>
            This order's coins have already been taken back.
          </p>
        ) : short ? (
          <p className="flex items-start gap-2 text-sm text-[#9C2C60]" role="alert">
            <span className="mt-0.5"><Icon name="alert" /></span>
            <span>
              This order earned {coins(order.coins_remaining)}, but the customer only has {customer.available_coins} free now,
              so some were already spent. Nothing can be deducted against this order.
            </span>
          </p>
        ) : (
          <div className="flex flex-wrap items-end gap-3">
            <label className="min-w-[16rem] flex-1">
              <span className="mb-1 block text-xs font-medium text-gray-600">Reason, saved with the deduction</span>
              <input value={reason} onChange={e => setReason(e.target.value)} maxLength={150} className={FIELD} />
            </label>
            <ArmedButton
              label={`Deduct ${coins(order.coins_remaining)}`}
              confirmLabel={`Confirm: take ${coins(order.coins_remaining)}`}
              armed={armedId === order.order_id}
              onArm={() => setArmedId(order.order_id)}
              onCancel={() => setArmedId(null)}
              onConfirm={() => onDeduct(order)}
              busy={busyId === order.order_id}
              disabled={!reasonOk}
            />
          </div>
        )}
      </div>
    </section>
  );
}

function CustomAmount({ customer, armed, setArmed, busy, onDeduct }) {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const n = parseInt(amount, 10);
  const valid = n > 0 && n <= customer.available_coins && reason.trim().length >= 3;
  return (
    <div className="rounded-xl border border-gray-200 bg-white">
      <button
        type="button"
        onClick={() => { setOpen(o => !o); setArmed(null); }}
        aria-expanded={open}
        className="flex w-full items-center justify-between rounded-xl px-5 py-3 text-left text-sm font-semibold text-gray-700 transition-colors duration-150 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zoddle-green/40"
      >
        Deduct a custom amount
        <span className={`text-gray-400 transition-transform duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] ${open ? 'rotate-180' : ''}`} aria-hidden>▾</span>
      </button>
      {open && (
        <div className="cd-enter flex flex-wrap items-end gap-3 border-t border-gray-100 px-5 py-4">
          <label className="w-32">
            <span className="mb-1 block text-xs font-medium text-gray-600">Coins</span>
            <input type="number" min="1" max={customer.available_coins} value={amount}
              onChange={e => { setAmount(e.target.value); setArmed(null); }} className={`${FIELD} tabular-nums`} />
          </label>
          <label className="min-w-[16rem] flex-1">
            <span className="mb-1 block text-xs font-medium text-gray-600">Reason, saved with the deduction</span>
            <input value={reason} onChange={e => setReason(e.target.value)} maxLength={150}
              placeholder="e.g. Goodwill credit reversed" className={FIELD} />
          </label>
          <ArmedButton
            label="Deduct"
            confirmLabel={valid ? `Confirm: take ${coins(n)}` : 'Confirm'}
            armed={armed}
            onArm={() => setArmed(true)}
            onCancel={() => setArmed(null)}
            onConfirm={() => onDeduct(n, reason.trim(), () => { setAmount(''); setReason(''); })}
            busy={busy}
            disabled={!valid}
          />
          <p className="basis-full text-xs text-gray-500">
            Up to {customer.available_coins} free coins. Coins already spent can't be deducted.
          </p>
        </div>
      )}
    </div>
  );
}

function PhonePanel({ result, armedId, setArmedId, busyId, onDeductOrder, onDeductCustom, reason, setReason }) {
  const { customer, customer_orders: orders } = result;
  const reasonOk = reason.trim().length >= 3;
  const totalEarned = orders.reduce((s, o) => s + o.coins_earned, 0);
  return (
    <div className="cd-enter space-y-4">
      <section className="rounded-xl border border-gray-200 bg-white p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h4 className="text-lg font-semibold tracking-tight text-gray-900">{customer.name || 'Unnamed customer'}</h4>
            <p className="tabular-nums text-sm text-gray-500">{customer.phone}</p>
          </div>
          <div className="text-right">
            <div className="text-3xl font-semibold tabular-nums tracking-tight text-gray-900">
              {customer.balance_coins}
              <span className="ml-1.5 text-base font-medium text-gray-500">in wallet</span>
            </div>
            <p className="text-sm text-gray-500 tabular-nums">
              {customer.available_coins === customer.balance_coins
                ? 'all free to deduct'
                : `${customer.available_coins} free to deduct, the rest held on an unpaid checkout`}
            </p>
          </div>
        </div>
      </section>

      <CustomAmount
        customer={customer}
        armed={armedId === 'custom'}
        setArmed={v => setArmedId(v ? 'custom' : null)}
        busy={busyId === 'custom'}
        onDeduct={onDeductCustom}
      />

      <section className="overflow-hidden rounded-xl border border-gray-200 bg-white">
        <div className="flex flex-wrap items-end justify-between gap-3 px-5 py-4">
          <div>
            <h4 className="text-sm font-semibold text-gray-900">Orders</h4>
            <p className="text-xs text-gray-500 tabular-nums">
              {orders.length} {orders.length === 1 ? 'order' : 'orders'} · {totalEarned} coins earned in total
            </p>
          </div>
          <label className="min-w-[16rem] max-w-md flex-1">
            <span className="mb-1 block text-xs font-medium text-gray-600">Reason for deductions below</span>
            <input value={reason} onChange={e => setReason(e.target.value)} maxLength={150} className={FIELD} />
          </label>
        </div>
        {orders.length === 0 ? (
          <p className="border-t border-gray-100 px-5 py-6 text-sm text-gray-500">
            This customer has no orders yet, so there is nothing earned to take back. Use the custom amount above to deduct from their wallet.
          </p>
        ) : (
          <div className="overflow-x-auto border-t border-gray-100">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs text-gray-500">
                <tr>
                  <th className="px-5 py-2 font-medium">Order</th>
                  <th className="px-3 py-2 font-medium">Date</th>
                  <th className="px-3 py-2 text-right font-medium">Paid</th>
                  <th className="px-3 py-2 text-right font-medium">Earned</th>
                  <th className="px-3 py-2 text-right font-medium">Taken back</th>
                  <th className="px-5 py-2 text-right font-medium">Deduct</th>
                </tr>
              </thead>
              <tbody>
                {orders.map(o => {
                  const earnedNothing = o.coins_earned === 0;
                  const free = customer.available_coins;
                  return (
                    <tr key={o.order_id} className={`border-t border-gray-100 ${earnedNothing ? 'text-gray-400' : 'text-gray-700'}`}>
                      <td className="px-5 py-3">
                        <div className="font-semibold text-gray-900">{o.order_number}</div>
                        <div className="text-xs text-gray-500">
                          {o.zocs_job_id ? `${o.zocs_job_id} · ` : ''}{o.status.replace(/_/g, ' ')}
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-3 py-3 text-gray-500">{when(o.created_at)}</td>
                      <td className="px-3 py-3 text-right tabular-nums">{rupees(o.total_paise)}</td>
                      <td className="px-3 py-3 text-right font-semibold tabular-nums">{o.coins_earned}</td>
                      <td className="px-3 py-3 text-right tabular-nums">{o.coins_deducted || '–'}</td>
                      <td className="px-5 py-3 text-right">
                        {o.coins_remaining === 0 ? (
                          <span className="text-xs text-gray-400">{earnedNothing ? 'No coins earned' : 'Taken back'}</span>
                        ) : o.coins_remaining > free ? (
                          <span className="text-xs text-[#9C2C60]" title="Some of these coins were already spent">
                            Only {free} free
                          </span>
                        ) : (
                          <ArmedButton
                            small
                            label={`Deduct ${o.coins_remaining}`}
                            confirmLabel={`Confirm ${o.coins_remaining}`}
                            armed={armedId === o.order_id}
                            onArm={() => setArmedId(o.order_id)}
                            onCancel={() => setArmedId(null)}
                            onConfirm={() => onDeductOrder(o)}
                            busy={busyId === o.order_id}
                            disabled={!reasonOk}
                          />
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function Skeleton() {
  return (
    <div className="cd-skeleton space-y-3" aria-hidden>
      <div className="h-28 rounded-xl bg-gray-100" />
      <div className="h-40 rounded-xl bg-gray-100" />
    </div>
  );
}

export default function CoinsDeductSection() {
  const [query, setQuery] = useState('');
  const [result, setResult] = useState(null);
  const [searched, setSearched] = useState('');
  const [searching, setSearching] = useState(false);
  const [armedId, setArmedId] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [reason, setReason] = useState(DEFAULT_REASON);
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  // Escape backs out of an armed deduct.
  useEffect(() => {
    if (!armedId) return undefined;
    const onKey = e => { if (e.key === 'Escape') setArmedId(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [armedId]);

  async function search(q, { keepNotice = false } = {}) {
    setSearching(true);
    setError(null);
    setArmedId(null);
    if (!keepNotice) setNotice(null);
    try {
      setResult(await lookupCoins(q));
      setSearched(q);
    } catch (err) {
      setResult(null);
      setError(errorMessage(err, 'Lookup failed'));
    } finally {
      setSearching(false);
    }
  }

  function onSearch(e) {
    e.preventDefault();
    const q = query.trim();
    if (q) search(q);
  }

  // `call` performs the write; the result is then re-read so the screen shows
  // the ledger as it now stands, not as it was.
  async function run(key, call, describe) {
    setBusyId(key);
    setError(null);
    setNotice(null);
    try {
      const res = await call();
      setNotice(describe(res));
      await search(searched, { keepNotice: true });
    } catch (err) {
      setArmedId(null);
      setError(errorMessage(err, 'Could not deduct the coins'));
    } finally {
      setBusyId(null);
    }
  }

  const whoLabel = c => c.name || c.phone;
  const took = res =>
    `Took back ${coins(res.coins_deducted)} from ${whoLabel(res.customer)} and notified them in the app. ` +
    `Their wallet is now ${res.balance_coins}.`;

  const deductOrder = order =>
    run(order.order_id, () => deductCoinsForOrder(order.order_id, { reason: reason.trim() }), took);

  const deductCustom = (n, why, reset) =>
    run('custom', () => deductCoinsForPhone({ phone: result.customer.phone, coins: n, reason: why }), res => {
      reset();
      return took(res);
    });

  const hint = describeQuery(query);

  return (
    <section className="max-w-4xl">
      <style>{CSS}</style>
      <h3 className="text-base font-semibold text-gray-900">Coins deduct</h3>
      <p className="mt-1 max-w-prose text-sm text-gray-500">
        Take coins back from a customer. Search an order or appointment id to deduct exactly what that order earned,
        or a phone number to see every order and deduct any amount.
      </p>

      <form onSubmit={onSearch} className="mt-5">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"><Icon name="search" /></span>
            <input
              ref={inputRef}
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Order id, appointment id or phone number"
              aria-label="Order id, appointment id or phone number"
              autoComplete="off"
              className={`${FIELD} !py-2.5 pl-9`}
            />
          </div>
          <button type="submit" disabled={searching || !query.trim()} className={`${BTN_DARK} !px-5`}>
            {searching ? 'Searching…' : 'Search'}
          </button>
        </div>
        <p className="mt-2 h-4 text-xs text-gray-500" aria-live="polite">{hint}</p>
      </form>

      <div className="mt-4 space-y-4" aria-live="polite">
        {notice && (
          <div className="cd-enter flex items-start gap-2 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800" role="status">
            <span className="mt-0.5"><Icon name="check" /></span>{notice}
          </div>
        )}
        {error && (
          <div className="cd-enter flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-[#9C2C60]" role="alert">
            <span className="mt-0.5"><Icon name="alert" /></span>{error}
          </div>
        )}

        {searching && !result && <Skeleton />}

        {result?.kind === 'order' && result.orders.map(o => (
          <OrderPanel key={o.order_id} order={o} armedId={armedId} setArmedId={setArmedId}
            busyId={busyId} onDeduct={deductOrder} reason={reason} setReason={setReason} />
        ))}

        {result?.kind === 'phone' && (
          <PhonePanel result={result} armedId={armedId} setArmedId={setArmedId} busyId={busyId}
            onDeductOrder={deductOrder} onDeductCustom={deductCustom} reason={reason} setReason={setReason} />
        )}

        {!result && !searching && !error && (
          <div className="rounded-xl border border-dashed border-gray-300 px-5 py-6 text-sm text-gray-600">
            <p className="font-medium text-gray-800">What you can search</p>
            <ul className="mt-2 space-y-1.5">
              <li><b className="text-gray-800">An order id</b>: see what that order earned and take exactly that back in one step.</li>
              <li><b className="text-gray-800">An appointment id</b>: the same, for the order placed on that booking.</li>
              <li><b className="text-gray-800">A phone number</b>: every order with its earnings, plus a custom amount.</li>
            </ul>
          </div>
        )}
      </div>
    </section>
  );
}
